import React, { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { motion, MotionConfig } from 'framer-motion';
import { usePlatformIntegrations } from './hooks/usePlatformIntegrations';
import { usePlatformWebSocket, usePlatformMetrics } from './hooks/usePlatformWebSocket';
import { useEcommerceIntegration } from './hooks/useIntegrations';
import { useRealTimeNotifications } from '@/features/conversations/hooks/useWebSocket';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import PlatformConnectionCard from './components/PlatformConnectionCard';
import { ConnectionGuide } from './components/ConnectionGuide';
import StoreConnectionCard from './components/StoreConnectionCard';
import WhatsAppConfigForm from './components/WhatsAppConfigForm';
import IntegrationsSummaryBar, { type SummaryItem } from './components/IntegrationsSummaryBar';
import { MarketplaceComingSoon } from './components/MarketplaceComingSoon';
import { WebChatEmbedCard } from './components/WebChatEmbedCard';
import { CategoryTabs, type IntegrationCategory } from './components/CategoryTabs';
import { messagingChannelMeta, storeChannelMeta } from './components/channelMeta';
import { containerVariants } from './components/motion';
import { CountUp } from '@/features/dashboard/components/CountUp';
import { MiniSparkBars } from '@/features/leads/components/LeadsStatsBar';
import { cn } from '@/lib/utils';
import {
  BarChart3,
  Link2,
  Loader2,
  CheckCircle,
  Settings,
  Users,
  TrendingUp,
  Activity,
  Clock,
  Zap,
  MessageSquare
} from 'lucide-react';
import type { WhatsAppConnectRequest } from '@/types/api';

const IntegrationsPage = () => {
  const [activeTab, setActiveTab] = useState('platforms');
  const [showWhatsAppForm, setShowWhatsAppForm] = useState(false);

  const {
    connectedPlatforms,
    isLoading,
    connectFacebook: connectFacebookMutation,
    connectInstagram: connectInstagramMutation,
    connectWhatsApp: connectWhatsAppMutation,
    disconnectPlatform,
    testConnection,
    isConnectingFacebook,
    isConnectingInstagram,
    isConnectingWhatsApp,
    isDisconnecting,
    isTesting,
    refetch
  } = usePlatformIntegrations();

  const {
    connections: storeConnections,
    isLoading: isLoadingStores,
    connectStore,
    connectStoreAsync,
    syncStore,
    disconnectStore,
    isConnecting: isConnectingStore,
    isSyncing: isSyncingStore,
    isDisconnecting: isDisconnectingStore,
  } = useEcommerceIntegration();

  // Wrapper functions to match expected signatures
  const connectFacebook = () => connectFacebookMutation('/integrations');
  const connectInstagram = () => connectInstagramMutation('/integrations');
  const connectWhatsApp = (data: WhatsAppConnectRequest) => {
    return new Promise((resolve, reject) => {
      connectWhatsAppMutation(data, {
        onSuccess: (result) => resolve(result),
        onError: (error) => reject(error)
      });
    });
  };
  const connectShopify = (shop: string) => connectStore({ platform: 'shopify', shop });
  const connectWooCommerce = (data: { siteUrl: string; consumerKey: string; consumerSecret: string }) =>
    connectStoreAsync({ platform: 'woocommerce', ...data });
  const connectAmazon = (data: { sellerId: string; clientId: string; clientSecret: string; refreshToken: string; marketplaceId?: string }) =>
    connectStoreAsync({ platform: 'amazon', ...data });
  const connectSalla = (data: { accessToken: string }) =>
    connectStoreAsync({ platform: 'salla', ...data });
  const connectEbay = (data: { accessToken: string }) =>
    connectStoreAsync({ platform: 'ebay', ...data });
  const connectLazada = (data: { accessToken: string; appKey: string }) =>
    connectStoreAsync({ platform: 'lazada', ...data });

  // Enable real-time notifications
  useRealTimeNotifications();

  // Get real-time platform updates
  const { recentUpdates } = usePlatformWebSocket();

  // Get real-time platform metrics
  const platformMetrics = usePlatformMetrics();

  const mainTabs = [
    { id: 'platforms', label: 'Connections', icon: Link2 },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, badge: 'Live' },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4 md:space-y-5">
          {/* Prominent High-Importance Top Tabs */}
          <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-800 pb-3">
            <div className="inline-flex items-center gap-1 rounded-2xl bg-slate-100/90 dark:bg-slate-850/90 p-1 border border-slate-200/80 dark:border-slate-750 shadow-2xs backdrop-blur-sm">
              {mainTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'relative flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition-all select-none',
                      isActive
                        ? 'text-slate-900 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-750/50'
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="active-main-integrations-tab"
                        className="absolute inset-0 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/80 shadow-xs"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-2">
                      <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500')} />
                      <span className="uppercase tracking-wider text-[11px]">{tab.label}</span>
                      {tab.badge && (
                        <span
                          className={cn(
                            'inline-flex items-center justify-center rounded-md px-1.5 py-0.2 text-[9px] font-bold tracking-tight border',
                            isActive
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/60'
                              : 'bg-slate-200/60 dark:bg-slate-750 text-slate-500 dark:text-slate-400 border-transparent'
                          )}
                        >
                          {tab.badge}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-5">
            {activeTab === 'platforms' && (
              <PlatformsTab
                connectedPlatforms={connectedPlatforms}
                isLoading={isLoading}
                showWhatsAppForm={showWhatsAppForm}
                setShowWhatsAppForm={setShowWhatsAppForm}
                connectFacebook={connectFacebook}
                connectInstagram={connectInstagram}
                connectWhatsApp={connectWhatsApp}
                disconnectPlatform={disconnectPlatform}
                testConnection={testConnection}
                isConnectingFacebook={isConnectingFacebook}
                isConnectingInstagram={isConnectingInstagram}
                isConnectingWhatsApp={isConnectingWhatsApp}
                isDisconnecting={isDisconnecting}
                isTesting={isTesting}
                refetch={refetch}
                recentUpdates={recentUpdates}
                storeConnections={storeConnections}
                isLoadingStores={isLoadingStores}
                connectShopify={connectShopify}
                connectWooCommerce={connectWooCommerce}
                connectAmazon={connectAmazon}
                connectSalla={connectSalla}
                connectEbay={connectEbay}
                connectLazada={connectLazada}
                syncStore={syncStore}
                disconnectStore={disconnectStore}
                isConnectingStore={isConnectingStore}
                isSyncingStore={isSyncingStore}
                isDisconnectingStore={isDisconnectingStore}
              />
            )}
            {activeTab === 'analytics' && (
              <AnalyticsTab
                connectedPlatforms={connectedPlatforms}
                platformMetrics={platformMetrics}
              />
            )}
            {activeTab === 'settings' && <SettingsTab />}
          </div>
        </div>
      </div>
    </MotionConfig>
  );
};

// Platforms Tab
interface PlatformsTabProps {
  connectedPlatforms: any;
  isLoading: boolean;
  showWhatsAppForm: boolean;
  setShowWhatsAppForm: (show: boolean) => void;
  connectFacebook: () => void;
  connectInstagram: () => void;
  connectWhatsApp: (data: WhatsAppConnectRequest) => Promise<any>;
  disconnectPlatform: (params: { platform: string; accountId: string }) => void;
  testConnection: (params: { platform: string; accountId: string }) => void;
  isConnectingFacebook: boolean;
  isConnectingInstagram: boolean;
  isConnectingWhatsApp: boolean;
  isDisconnecting: boolean;
  isTesting: boolean;
  refetch: () => void;
  recentUpdates: Array<{
    id: string;
    type: string;
    platform: string;
    message: string;
    timestamp: Date;
  }>;
  storeConnections: any[];
  isLoadingStores: boolean;
  connectShopify: (shop: string) => void;
  connectWooCommerce: (data: { siteUrl: string; consumerKey: string; consumerSecret: string }) => Promise<unknown>;
  connectAmazon: (data: { sellerId: string; clientId: string; clientSecret: string; refreshToken: string; marketplaceId?: string }) => Promise<unknown>;
  connectSalla: (data: { accessToken: string }) => Promise<unknown>;
  connectEbay: (data: { accessToken: string }) => Promise<unknown>;
  connectLazada: (data: { accessToken: string; appKey: string }) => Promise<unknown>;
  syncStore: (connectionId: string) => void;
  disconnectStore: (connectionId: string) => void;
  isConnectingStore: boolean;
  isSyncingStore: boolean;
  isDisconnectingStore: boolean;
}

const PlatformsTab: React.FC<PlatformsTabProps> = ({
  connectedPlatforms,
  isLoading,
  showWhatsAppForm,
  setShowWhatsAppForm,
  connectFacebook,
  connectInstagram,
  connectWhatsApp,
  disconnectPlatform,
  testConnection,
  isConnectingFacebook,
  isConnectingInstagram,
  isConnectingWhatsApp,
  isDisconnecting,
  isTesting,
  refetch,
  recentUpdates,
  storeConnections,
  isLoadingStores,
  connectShopify,
  connectWooCommerce,
  connectAmazon,
  connectSalla,
  connectEbay,
  connectLazada,
  syncStore,
  disconnectStore,
  isConnectingStore,
  isSyncingStore,
  isDisconnectingStore,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory>('all');

  if (isLoading || isLoadingStores) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleWhatsAppConnect = (platform: string) => {
    if (platform === 'whatsapp') {
      setShowWhatsAppForm(true);
    }
  };

  const handleWhatsAppSubmit = async (data: WhatsAppConnectRequest) => {
    const result = await connectWhatsApp(data);
    if (result) {
      setShowWhatsAppForm(false);
      refetch();
    }
    return result;
  };

  if (showWhatsAppForm) {
    return (
      <WhatsAppConfigForm
        onSubmit={handleWhatsAppSubmit}
        isLoading={isConnectingWhatsApp}
        onCancel={() => setShowWhatsAppForm(false)}
      />
    );
  }

  const shopifyConnections = storeConnections.filter((c) => c.platform === 'shopify');
  const woocommerceConnections = storeConnections.filter((c) => c.platform === 'woocommerce');
  const amazonConnections = storeConnections.filter((c) => c.platform === 'amazon');
  const sallaConnections = storeConnections.filter((c) => c.platform === 'salla');
  const ebayConnections = storeConnections.filter((c) => c.platform === 'ebay');
  const lazadaConnections = storeConnections.filter((c) => c.platform === 'lazada');

  const fbCount = connectedPlatforms?.accounts.facebook?.length || 0;
  const igCount = connectedPlatforms?.accounts.instagram?.length || 0;
  const waCount = connectedPlatforms?.accounts.whatsapp?.length || 0;

  const messagingConnectedCount = (fbCount > 0 ? 1 : 0) + (igCount > 0 ? 1 : 0) + (waCount > 0 ? 1 : 0);
  const commerceConnectedCount =
    (shopifyConnections.length > 0 ? 1 : 0) +
    (woocommerceConnections.length > 0 ? 1 : 0) +
    (amazonConnections.length > 0 ? 1 : 0) +
    (sallaConnections.length > 0 ? 1 : 0) +
    (ebayConnections.length > 0 ? 1 : 0) +
    (lazadaConnections.length > 0 ? 1 : 0);
  const totalConnectedCount = messagingConnectedCount + commerceConnectedCount;

  const summaryItems: SummaryItem[] = [
    {
      key: 'facebook',
      name: messagingChannelMeta.facebook.name,
      icon: messagingChannelMeta.facebook.icon,
      connected: fbCount > 0,
      count: fbCount,
    },
    {
      key: 'instagram',
      name: messagingChannelMeta.instagram.name,
      icon: messagingChannelMeta.instagram.icon,
      connected: igCount > 0,
      count: igCount,
    },
    {
      key: 'whatsapp',
      name: messagingChannelMeta.whatsapp.name,
      icon: messagingChannelMeta.whatsapp.icon,
      connected: waCount > 0,
      count: waCount,
    },
    {
      key: 'shopify',
      name: storeChannelMeta.shopify.name,
      icon: storeChannelMeta.shopify.icon,
      connected: shopifyConnections.length > 0,
      count: shopifyConnections.length,
    },
    {
      key: 'woocommerce',
      name: storeChannelMeta.woocommerce.name,
      icon: storeChannelMeta.woocommerce.icon,
      connected: woocommerceConnections.length > 0,
      count: woocommerceConnections.length,
    },
    {
      key: 'amazon',
      name: storeChannelMeta.amazon.name,
      icon: storeChannelMeta.amazon.icon,
      connected: amazonConnections.length > 0,
      count: amazonConnections.length,
    },
    {
      key: 'salla',
      name: storeChannelMeta.salla.name,
      icon: storeChannelMeta.salla.icon,
      connected: sallaConnections.length > 0,
      count: sallaConnections.length,
    },
    {
      key: 'ebay',
      name: storeChannelMeta.ebay.name,
      icon: storeChannelMeta.ebay.icon,
      connected: ebayConnections.length > 0,
      count: ebayConnections.length,
    },
    {
      key: 'lazada',
      name: storeChannelMeta.lazada.name,
      icon: storeChannelMeta.lazada.icon,
      connected: lazadaConnections.length > 0,
      count: lazadaConnections.length,
    },
  ];

  return (
    <motion.div className="space-y-5" variants={containerVariants} initial="hidden" animate="visible">
      {/* Compact connected-count summary */}
      <motion.div variants={containerVariants}>
        <IntegrationsSummaryBar items={summaryItems} />
      </motion.div>

      {/* Setup guidance: requirements + how to connect FB / Instagram */}
      <motion.div variants={containerVariants}>
        <ConnectionGuide />
      </motion.div>

      {/* Category Tab Component for clean category navigation */}
      <div className="pt-1">
        <CategoryTabs
          activeCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          counts={{
            messaging: messagingConnectedCount,
            commerce: commerceConnectedCount,
            total: totalConnectedCount,
          }}
        />
      </div>

      {/* Messaging Channels */}
      {(selectedCategory === 'all' || selectedCategory === 'messaging') && (
        <motion.div variants={containerVariants} layout className="space-y-2.5">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Messaging channels
            </h2>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
              Automated inbox replies
            </span>
          </div>
          <motion.div
            className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3"
            variants={containerVariants}
          >
            <div data-copilot-target="integrations-facebook">
              <PlatformConnectionCard
                platform="facebook"
                isConnected={fbCount > 0}
                accounts={connectedPlatforms?.accounts.facebook || []}
                onConnect={() => connectFacebook()}
                onTest={(accountId) => testConnection({ platform: 'facebook', accountId })}
                onDisconnect={(accountId) => disconnectPlatform({ platform: 'facebook', accountId })}
                isConnecting={isConnectingFacebook}
                isTesting={isTesting}
                isDisconnecting={isDisconnecting}
              />
            </div>

            <div data-copilot-target="integrations-instagram">
              <PlatformConnectionCard
                platform="instagram"
                isConnected={igCount > 0}
                accounts={connectedPlatforms?.accounts.instagram || []}
                onConnect={() => connectInstagram()}
                onTest={(accountId) => testConnection({ platform: 'instagram', accountId })}
                onDisconnect={(accountId) => disconnectPlatform({ platform: 'instagram', accountId })}
                isConnecting={isConnectingInstagram}
                isTesting={isTesting}
                isDisconnecting={isDisconnecting}
              />
            </div>

            <div data-copilot-target="integrations-whatsapp">
              <PlatformConnectionCard
                platform="whatsapp"
                isConnected={waCount > 0}
                accounts={connectedPlatforms?.accounts.whatsapp || []}
                onConnect={() => handleWhatsAppConnect('whatsapp')}
                onTest={(accountId) => testConnection({ platform: 'whatsapp', accountId })}
                onDisconnect={(accountId) => disconnectPlatform({ platform: 'whatsapp', accountId })}
                isConnecting={isConnectingWhatsApp}
                isTesting={isTesting}
                isDisconnecting={isDisconnecting}
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Commerce Stores */}
      {(selectedCategory === 'all' || selectedCategory === 'commerce') && (
        <motion.div variants={containerVariants} layout className="space-y-2.5">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Commerce stores &amp; Marketplaces
            </h2>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
              Product catalog, stock &amp; order data sync
            </span>
          </div>
          <motion.div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3" variants={containerVariants}>
            <div data-copilot-target="integrations-shopify">
              <StoreConnectionCard
                platform="shopify"
                connections={shopifyConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>

            <div data-copilot-target="integrations-woocommerce">
              <StoreConnectionCard
                platform="woocommerce"
                connections={woocommerceConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>

            <div data-copilot-target="integrations-amazon">
              <StoreConnectionCard
                platform="amazon"
                connections={amazonConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>

            <div data-copilot-target="integrations-salla">
              <StoreConnectionCard
                platform="salla"
                connections={sallaConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>

            <div data-copilot-target="integrations-ebay">
              <StoreConnectionCard
                platform="ebay"
                connections={ebayConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>

            <div data-copilot-target="integrations-lazada">
              <StoreConnectionCard
                platform="lazada"
                connections={lazadaConnections}
                onConnectShopify={connectShopify}
                onConnectWooCommerce={connectWooCommerce}
                onConnectAmazon={connectAmazon}
                onConnectSalla={connectSalla}
                onConnectEbay={connectEbay}
                onConnectLazada={connectLazada}
                onSync={syncStore}
                onDisconnect={disconnectStore}
                isConnecting={isConnectingStore}
                isSyncing={isSyncingStore}
                isDisconnecting={isDisconnectingStore}
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Website Chat embed */}
      {(selectedCategory === 'all' || selectedCategory === 'webchat') && (
        <motion.div variants={containerVariants} layout data-copilot-target="integrations-webchat">
          <WebChatEmbedCard />
        </motion.div>
      )}

      {/* Marketplaces (roadmap) */}
      {(selectedCategory === 'all' || selectedCategory === 'marketplaces') && (
        <motion.div variants={containerVariants} layout>
          <MarketplaceComingSoon />
        </motion.div>
      )}

      {/* Recent Activity */}
      {recentUpdates.length > 0 && (
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
          <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Activity className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                Recent Activity
              </span>
            </div>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Live webhook &amp; connection log</span>
          </div>
          <div className="p-3 bg-white/70 dark:bg-slate-900/70">
            <div className="space-y-1.5">
              {recentUpdates.slice(0, 5).map((update) => (
                <div
                  key={update.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-3 py-2 shadow-2xs"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        update.type === 'connected'
                          ? 'bg-emerald-500'
                          : update.type === 'disconnected'
                            ? 'bg-rose-500'
                            : update.type === 'test_success'
                              ? 'bg-sky-500'
                              : update.type === 'test_failed'
                                ? 'bg-amber-500'
                                : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">{update.platform}</p>
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{update.message}</p>
                    </div>
                  </div>
                  <div className="shrink-0 text-[10px] font-medium text-slate-400 dark:text-slate-500">
                    {update.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </motion.div>
  );
};

// Compact Metric Capsule
interface CompactMetricTileProps {
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  icon: React.ElementType;
  bars?: number[];
  barTone?: 'emerald' | 'primary' | 'rose' | 'amber' | 'slate';
  description?: string;
}

function CompactMetricTile({
  label,
  value,
  suffix = '',
  prefix = '',
  decimals = 0,
  icon: Icon,
  bars,
  barTone,
  description,
}: CompactMetricTileProps) {
  const isZero = value === 0;
  const activeBars = isZero ? [20, 20, 20, 20, 20] : (bars || [30, 50, 70, 95, 80]);
  const activeTone = isZero ? 'slate' : (barTone || 'emerald');

  const content = (
    <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-850 px-2.5 py-2 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-xs cursor-pointer w-full">
      {/* Left Icon Pill */}
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800 text-slate-700 dark:text-slate-300 transition-all duration-200 group-hover/tile:bg-primary/10 group-hover/tile:text-primary">
        <Icon className="h-3.5 w-3.5" />
      </div>

      {/* Right Metric Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
            <CountUp value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
          </span>
          <MiniSparkBars bars={activeBars} tone={activeTone} />
        </div>
        <span className="inline-block text-[9.5px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 transition-colors whitespace-nowrap">
          {label}
        </span>
      </div>
    </div>
  );

  if (!description) return content;

  return (
    <Tooltip content={description} placement="top" className="block w-full">
      {content}
    </Tooltip>
  );
}

// Analytics Tab
interface AnalyticsTabProps {
  connectedPlatforms: any;
  platformMetrics: {
    facebook: { messages: number; conversations: number; responseRate: number };
    instagram: { messages: number; conversations: number; responseRate: number };
    whatsapp: { messages: number; conversations: number; responseRate: number };
    total: { messages: number; conversations: number; responseRate: number };
  };
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ connectedPlatforms, platformMetrics }) => {
  const totalMessages = platformMetrics.total.messages;
  const totalConversations = platformMetrics.total.conversations;
  const avgResponseRate = platformMetrics.total.responseRate;

  const platforms = [
    {
      key: 'facebook',
      name: 'Facebook Messenger',
      meta: messagingChannelMeta.facebook,
      metrics: platformMetrics.facebook,
    },
    {
      key: 'instagram',
      name: 'Instagram Business',
      meta: messagingChannelMeta.instagram,
      metrics: platformMetrics.instagram,
    },
    {
      key: 'whatsapp',
      name: 'WhatsApp Business',
      meta: messagingChannelMeta.whatsapp,
      metrics: platformMetrics.whatsapp,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Real-time Metrics Overview Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Zap className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Real-Time Platform Performance
            </span>
          </div>
          <Tooltip content="Live sync from webhook and omnichannel events" placement="top">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300 cursor-pointer">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Sync
            </span>
          </Tooltip>
        </div>

        <div className="p-2.5 sm:p-3 bg-white/70 dark:bg-slate-900/70">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <CompactMetricTile
              label="MESSAGES TODAY"
              value={totalMessages}
              icon={MessageSquare}
              bars={[40, 65, 85, 100, 75]}
              barTone="primary"
              description="Total customer messages received and processed today"
            />
            <CompactMetricTile
              label="ACTIVE CONVERSATIONS"
              value={totalConversations}
              icon={Users}
              bars={[30, 50, 70, 95, 60]}
              barTone="emerald"
              description="Ongoing customer conversation threads across all channels"
            />
            <CompactMetricTile
              label="AVG RESPONSE RATE"
              value={avgResponseRate}
              suffix="%"
              icon={TrendingUp}
              bars={[45, 60, 80, 100, 85]}
              barTone="emerald"
              description="Overall AI and agent reply completion rate"
            />
            <CompactMetricTile
              label="CONNECTED HUBS"
              value={connectedPlatforms?.summary?.total || 0}
              icon={Activity}
              bars={[90, 70, 50, 30, 20]}
              barTone="slate"
              description="Active live integrations feeding into this workspace"
            />
          </div>
        </div>
      </Card>

      {/* Channel Breakdown Cards with compacted height and uniform design */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">Channel breakdown</h2>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Per-platform conversation telemetry</span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {platforms.map((p) => {
            const Icon = p.meta.icon;
            return (
              <Card
                key={p.key}
                className="flex flex-col overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm"
              >
                {/* Pod Header */}
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={cn('flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-lg shadow-2xs', p.meta.classes.iconBg)}>
                      <Icon className={cn('h-3.5 w-3.5', p.meta.classes.iconText)} />
                    </div>
                    <h3 className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                      {p.meta.name}
                    </h3>
                  </div>
                  <Tooltip content={`Response success rate for ${p.meta.name}`} placement="top">
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300 cursor-pointer">
                      {p.metrics.responseRate}% rate
                    </span>
                  </Tooltip>
                </div>

                {/* Pod Body */}
                <div className="flex flex-1 flex-col justify-between p-2.5 bg-white/70 dark:bg-slate-900/70 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <CompactMetricTile
                      label="MESSAGES"
                      value={p.metrics.messages}
                      icon={MessageSquare}
                      bars={[30, 50, 70, 95, 60]}
                      barTone="primary"
                      description={`Total messages processed via ${p.meta.name}`}
                    />
                    <CompactMetricTile
                      label="CONVERSATIONS"
                      value={p.metrics.conversations}
                      icon={Users}
                      bars={[40, 65, 85, 100, 75]}
                      barTone="emerald"
                      description={`Active conversation threads on ${p.meta.name}`}
                    />
                  </div>

                  <div className="pt-1.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                    <span>Performance status</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Operational
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Settings Tab
const SettingsTab = () => {
  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Settings className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Global Platform Configuration
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Applies across all connected channels</span>
      </div>

      <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
        <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs flex items-center justify-between gap-3">
          <div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Auto-retry failed webhooks</span>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Automatically retry event deliveries on intermittent failure</p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
            Enabled
          </span>
        </div>

        <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs flex items-center justify-between gap-3">
          <div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Real-time message alerts</span>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Receive instant push notifications for new high-intent incoming leads</p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
            Enabled
          </span>
        </div>
      </div>
    </Card>
  );
};

export default IntegrationsPage;
